/**
 * VersionUploadZone Component
 *
 * A compact file upload zone for adding new versions to existing tables.
 * Designed to fit inline in the versions tab of the preview panel.
 *
 * Features:
 * - Drag and drop file upload
 * - Click to browse files
 * - Inline progress indicator during upload
 * - Processing status polling after upload
 * - Support for .csv, .json, .xlsx, .parquet files
 * - State persistence for recovery after panel close/reopen
 *
 * Uses the useVersionUpload hook for upload logic (single source of truth).
 * Uses useVersionUploadStore for state persistence.
 *
 * @module features/workspace/components/PreviewPanel/VersionUploadZone
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { GitMerge, Upload, X } from 'lucide-react';
import type { VersionStatus } from '@loader/shared-types';
import { VERSION_IN_PROGRESS_STATUSES } from '@loader/shared-types';
import { Button, Separator } from '@loader/ui';

import { FileDropzone } from '@/components/common';
import type { UploadState } from '@/components/common/FileDropzone';
import { useVersionUpload } from '@/features/workspace/hooks';
import {
  useVersionUploadStore,
  useTableUploadState,
} from '@/features/workspace/stores/useVersionUploadStore';
import { useVersionProgress } from '@/hooks/useVersionProgress';
import { useVersionWithStorageLimit } from '@/hooks';
import { isStorageLimitError } from '@/lib/errorUtils';
import { getUserFriendlyError } from '@/lib/fileUtils';
import {
  UPLOAD_PHASE_WEIGHT,
  PROCESSING_PHASE_WEIGHT,
  UPLOAD_PHASE_END_PERCENT,
  type UploadVisualState,
} from '@/lib/uploadTypes';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Delay before resetting to idle after showing error/completion status (ms) */
const STATUS_DISPLAY_DELAY_MS = 3000;

/** Delay before resetting to idle after successful completion (ms) */
const SUCCESS_DISPLAY_DELAY_MS = 2000;

// ============================================================================
// TYPES
// ============================================================================

interface VersionUploadZoneProps {
  /** Table ID to upload the version to */
  tableId: string;
  /** Pre-selected file (e.g., from drag-drop on preview panel) */
  preselectedFile?: File | null;
  /** Callback when pre-selected file is cleared */
  onClearPreselectedFile?: () => void;
  /** Callback when upload completes successfully (after processing) */
  onUploadComplete?: (versionId: string) => void;
  /** Callback when upload fails */
  onUploadError?: (error: string) => void;
  /** Disable the upload zone */
  disabled?: boolean;
  /** Selected base version ID for append mode (controlled from parent) */
  selectedBaseVersionId?: string | null;
  /** Callback to clear the selected base version */
  onClearBaseVersion?: () => void;
  /** Callback when file staged state changes */
  onFileStagedChange?: (isStaged: boolean) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function VersionUploadZone({
  tableId,
  preselectedFile,
  onClearPreselectedFile,
  onUploadComplete,
  onUploadError,
  disabled = false,
  selectedBaseVersionId,
  onClearBaseVersion,
  onFileStagedChange,
}: VersionUploadZoneProps) {
  // Use centralized upload hook
  const {
    uploadFile,
    uploadProgress,
    error: uploadError,
    resetError,
    resetProgress,
  } = useVersionUpload();

  // Storage limit check with paywall dialog
  const { checkStorageBeforeUpload, StorageLimitDialog, setShowLimitDialog } =
    useVersionWithStorageLimit();

  // Persisted upload state from store
  const persistedState = useTableUploadState(tableId);
  const { setUpload, updateVersionStatus, updateVisualState, updateProgress, clearUpload } =
    useVersionUploadStore();

  // Local UI state - initialized from persisted state if available
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(
    () => persistedState?.versionId ?? null
  );
  const [visualState, setVisualState] = useState<UploadVisualState>(
    () => persistedState?.visualState ?? 'idle'
  );
  const [displayError, setDisplayError] = useState<string | null>(null);
  // Staged file - either from local selection or preselectedFile prop
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  // Excel sheet selection state (managed by FileDropzone)
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);

  // Track if we've called onUploadComplete for this version
  const hasCalledCompleteRef = useRef<string | null>(null);
  // Track max progress seen to ensure monotonically increasing progress display
  // Initialize from persisted state for smooth recovery
  const maxProgressRef = useRef<number>(persistedState?.progress ?? 0);
  // Track if we've restored from persisted state
  const hasRestoredRef = useRef(false);

  // Poll version processing status after upload (or from restored state)
  const { version: polledVersion } = useVersionProgress(currentVersionId ?? undefined);

  // Effective selected file (prop takes precedence if provided)
  const selectedFile = preselectedFile ?? stagedFile;

  // ============================================================================
  // RESTORE PERSISTED STATE ON MOUNT
  // ============================================================================

  useEffect(() => {
    // Only restore once on mount
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    if (persistedState && persistedState.versionId) {
      // Check if the upload is still in progress (useVersionUploadStore.isInProgress
      // already handles staleness, so if we have a persistedState it's not stale)
      const isStillInProgress =
        persistedState.visualState === 'uploading' ||
        persistedState.visualState === 'processing' ||
        (persistedState.versionStatus &&
          VERSION_IN_PROGRESS_STATUSES.includes(persistedState.versionStatus));

      if (isStillInProgress) {
        // Restore state - polling will resume automatically via useVersionProgress
        setCurrentVersionId(persistedState.versionId);
        setVisualState(persistedState.visualState);
      } else {
        // Persisted state is stale (completed/failed/timed out), clear it
        clearUpload(tableId);
      }
    }
  }, [persistedState, tableId, clearUpload]);

  // Notify parent when file staged state changes (handles preselectedFile too)
  useEffect(() => {
    onFileStagedChange?.(!!selectedFile);
  }, [selectedFile, onFileStagedChange]);

  // ============================================================================
  // FILE SELECTION HANDLER (stages file, doesn't upload)
  // ============================================================================

  /**
   * Handle file selection - stages the file for upload
   */
  const handleFileSelect = useCallback((file: File) => {
    setStagedFile(file);
    onFileStagedChange?.(true);
  }, [onFileStagedChange]);

  /**
   * Clear the staged/selected file
   */
  const handleClearFile = useCallback(() => {
    setStagedFile(null);
    onClearPreselectedFile?.();
    onFileStagedChange?.(false);
    onClearBaseVersion?.();
  }, [onClearPreselectedFile, onFileStagedChange, onClearBaseVersion]);

  // ============================================================================
  // UPLOAD HANDLER
  // ============================================================================

  /**
   * Trigger upload of the selected file
   */
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    // Reset progress tracking for new upload
    maxProgressRef.current = 0;
    resetProgress(); // Reset hook's progress to avoid stale value from previous upload

    // Set visual state to uploading immediately when user clicks upload
    // This ensures the UI shows upload state even during storage check
    setVisualState('uploading');

    // Check storage limit before uploading - shows paywall dialog if limit reached
    const canProceed = await checkStorageBeforeUpload(selectedFile.size);
    if (!canProceed) {
      // Reset file selection so user can try with a different/smaller file
      setStagedFile(null);
      setSelectedSheet(null);
      setVisualState('idle');
      onClearPreselectedFile?.();
      return; // Dialog will be shown by the hook
    }

    // Use the hook's uploadFile function
    const result = await uploadFile(selectedFile, tableId, {
      sheetName: selectedSheet ?? undefined,
      baseVersionId: selectedBaseVersionId ?? undefined,
    });

    if (result?.versionId) {
      // Start polling for processing status
      // Set versionId first, then visual state to ensure polling starts
      setCurrentVersionId(result.versionId);
      setVisualState('processing');

      // Persist state to store for recovery
      setUpload(tableId, {
        versionId: result.versionId,
        visualState: 'processing',
        versionStatus: null,
      });

      // Clear the staged file after upload starts
      setStagedFile(null);
      onClearPreselectedFile?.();
    } else {
      // Upload failed - the error effect will handle showing error state
      // But if no error was set (unexpected case), reset to idle
      if (!uploadError) {
        setVisualState('idle');
      }
    }
  }, [
    selectedFile,
    uploadFile,
    tableId,
    selectedSheet,
    selectedBaseVersionId,
    onClearPreselectedFile,
    checkStorageBeforeUpload,
    uploadError,
    setUpload,
    resetProgress,
  ]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Reset to idle state and clear persisted store
   */
  const resetToIdle = useCallback(() => {
    setVisualState('idle');
    setCurrentVersionId(null);
    setDisplayError(null);
    setStagedFile(null);
    setSelectedSheet(null);
    onClearBaseVersion?.();
    hasCalledCompleteRef.current = null;
    maxProgressRef.current = 0;
    resetError();
    onClearPreselectedFile?.();

    // Clear persisted state
    clearUpload(tableId);
  }, [resetError, onClearPreselectedFile, onClearBaseVersion, clearUpload, tableId]);

  // ============================================================================
  // SYNC UPLOAD STATE
  // ============================================================================

  // Note: We no longer sync isUploading to visualState via effect.
  // The visual state is set explicitly in handleUpload to avoid race conditions
  // with fast uploads where isUploading goes true->false before React re-renders.

  /**
   * Sync error from upload hook
   * If error is storage-related, show paywall instead of error view
   */
  useEffect(() => {
    if (uploadError) {
      // Check if this is a storage limit error from the backend
      const isStorageError = isStorageLimitError(uploadError);

      if (isStorageError) {
        // Show paywall instead of error view
        setShowLimitDialog(true);
        resetToIdle();
        return;
      }

      setVisualState('failed');
      const friendlyError = getUserFriendlyError(uploadError);
      setDisplayError(friendlyError);
      onUploadError?.(friendlyError);

      // Reset to idle after delay
      const timer = setTimeout(() => {
        resetToIdle();
      }, STATUS_DISPLAY_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [uploadError, onUploadError, resetToIdle, setShowLimitDialog]);

  /**
   * Sync version processing status from polling
   */
  useEffect(() => {
    if (!polledVersion) return;

    const status = polledVersion.status as VersionStatus;

    // Update store with latest status
    if (currentVersionId) {
      updateVersionStatus(tableId, status);
    }

    if (status === 'queued' || status === 'pending' || status === 'processing') {
      setVisualState('processing');
      updateVisualState(tableId, 'processing');
    } else if (status === 'completed') {
      setVisualState('completed');

      // Call onUploadComplete once per version
      if (hasCalledCompleteRef.current !== polledVersion.id) {
        hasCalledCompleteRef.current = polledVersion.id;
        onUploadComplete?.(polledVersion.id);

        // Reset to idle after delay
        setTimeout(() => {
          resetToIdle();
        }, SUCCESS_DISPLAY_DELAY_MS);
      }
    } else if (status === 'failed') {
      setVisualState('failed');
      const errorMsg = polledVersion.error_message || 'Version processing failed';
      const friendlyError = getUserFriendlyError(errorMsg);
      setDisplayError(friendlyError);
      onUploadError?.(friendlyError);

      // Reset to idle after delay
      setTimeout(() => {
        resetToIdle();
      }, STATUS_DISPLAY_DELAY_MS);
    }
  }, [
    polledVersion,
    onUploadComplete,
    onUploadError,
    resetToIdle,
    currentVersionId,
    tableId,
    updateVersionStatus,
    updateVisualState,
  ]);

  // ============================================================================
  // DERIVED STATE FOR DROPZONE
  // ============================================================================

  /**
   * Map visual state to FileDropzone uploadState
   */
  const getDropzoneState = (): UploadState => {
    return visualState as UploadState;
  };

  /**
   * Get current progress percentage (0-100)
   *
   * Maps upload and processing phases to a combined range to prevent backward jumps:
   * - Upload phase (0-100%) maps to display (0-80%)
   * - Processing phase (0-100%) maps to display (80-100%)
   *
   * This ensures smooth progression: user sees 0→80 during upload, then 80→100
   * during processing, with no jarring backward jumps at phase transition.
   */
  const getProgress = (): number => {
    let mappedProgress = 0;

    if (visualState === 'uploading') {
      // Map upload progress (0-100) to display range (0-80)
      mappedProgress = Math.round(uploadProgress * UPLOAD_PHASE_WEIGHT);
    } else if (visualState === 'processing') {
      // Map processing progress (0-100) to display range (80-100)
      const processingProgress = polledVersion?.progress ?? 0;
      mappedProgress = UPLOAD_PHASE_END_PERCENT + Math.round(processingProgress * PROCESSING_PHASE_WEIGHT);
    } else if (visualState === 'completed') {
      mappedProgress = 100;
    }

    // Ensure monotonically increasing progress (never go backwards)
    if (mappedProgress > maxProgressRef.current) {
      maxProgressRef.current = mappedProgress;
    }

    return maxProgressRef.current;
  };

  // Computed progress value for rendering and persistence
  const currentProgress = getProgress();

  // Persist progress to store for recovery when panel is closed/reopened
  useEffect(() => {
    if (currentVersionId && currentProgress > 0) {
      updateProgress(tableId, currentProgress);
    }
  }, [currentVersionId, currentProgress, tableId, updateProgress]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Show upload button when file is selected and we're in idle state
  const showUploadButton = selectedFile && visualState === 'idle';

  return (
    <>
      <div className="space-y-2">
        <FileDropzone
          variant="inline"
          onFileSelect={handleFileSelect}
          onClearFile={handleClearFile}
          selectedFile={selectedFile}
          uploadState={getDropzoneState()}
          uploadProgress={currentProgress}
          errorMessage={displayError ?? undefined}
          disabled={disabled}
          onSheetChange={setSelectedSheet}
          selectedSheet={selectedSheet}
        />
        {/* Upload button and append indicator - shown when file selected */}
        {showUploadButton && (
          <div className="space-y-2">
            {/* Append indicator - shown when a base version is selected */}
            {selectedBaseVersionId && (
              <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                <GitMerge className="size-4 shrink-0 text-primary" strokeWidth={1.5} />
                <span className="flex-1 truncate text-sm">
                  Append to selected version
                </span>
                <button
                  type="button"
                  onClick={onClearBaseVersion}
                  className="rounded-sm p-0.5 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <X className="size-3.5 text-muted-foreground" />
                </button>
              </div>
            )}

            <Button
              size="sm"
              className="w-full"
              onClick={handleUpload}
              disabled={disabled}
            >
              <Upload className="mr-2 size-4" />
              {selectedBaseVersionId ? 'Upload & Append' : 'Upload Version'}
            </Button>

            <Separator />
          </div>
        )}
      </div>

      {/* Storage Limit Dialog - rendered by the hook */}
      <StorageLimitDialog />
    </>
  );
}
